/**
 * Раздел «Настройки» со стороны экрана (спецификация § 14).
 *
 * Правка видна сразу, не дожидаясь сервера: человек переключает тему и видит тему, а не
 * задумчивое ожидание. Сервер отказал — всё возвращается ровно к прежнему виду и человеку
 * говорят словами, что не вышло. Молчаливого отката без объяснения не бывает: экран,
 * сам собой вернувшийся к светлой теме, читается как поломка.
 *
 * Вёрстки и браузера здесь нет (ADR-0002). Готовое оформление считает `appearance.ts`,
 * а прикладывать его к экрану — дело оболочки.
 */
import { appearanceOf } from './appearance.js';
import type { Appearance, DeviceHints } from './appearance.js';

/** Настройки, как их отдаёт сервер: пары «ключ — значение», без вложенности. */
export type Settings = Readonly<Record<string, string | number | boolean>>;

/** Раздел «Настройки» разбит на группы — их состав приходит с сервером (§ 14.1–14.9). */
export type SettingGroups = Readonly<Record<string, readonly string[]>>;

/** Ответ сервера на любую работу с Настройками. */
export interface SettingsAnswer {
  settings: Settings;
  /** Что человек задал сам: только это и имеет смысл «сбрасывать». */
  ownKeys: readonly string[];
  /** Что сервер не понял и вернул к прежнему — обычно пусто. */
  ignoredKeys: readonly string[];
  groups: SettingGroups;
}

/** Дверь к серверу. Единственное место, знающее про сеть, — за этим договором. */
export interface SettingsGateway {
  read(): Promise<SettingsAnswer>;
  update(patch: Record<string, string | number | boolean>): Promise<SettingsAnswer>;
  reset(): Promise<SettingsAnswer>;
}

/** Что видно на экране прямо сейчас. */
export interface SettingsState {
  readonly settings: Settings;
  readonly ownKeys: readonly string[];
  readonly ignoredKeys: readonly string[];
  readonly groups: SettingGroups;
  /** Готовое оформление — его оболочка и применяет. */
  readonly appearance: Appearance;
  readonly loading: boolean;
  readonly saving: boolean;
  readonly refusal: string | null;
}

export interface SettingsEditor {
  state(): SettingsState;
  subscribe(listener: (state: SettingsState) => void): () => void;
  load(): Promise<void>;
  /** Поменять одну Настройку. Видно сразу, сервер догоняет. */
  change(key: string, value: string | number | boolean): Promise<void>;
  /** Сброс к настройкам фирмы (§ 14.9). */
  reset(): Promise<void>;
  /** Подсказки устройства поменялись — например, система перешла на тёмное оформление. */
  updateHints(hints: DeviceHints): void;
  dismissRefusal(): void;
}

const explain = (error: unknown): string => {
  const said = error instanceof Error ? error.message.trim() : '';
  return said || 'Не получилось сохранить — проверьте связь и попробуйте ещё раз';
};

const BUSY_SAVING = 'Предыдущая правка ещё сохраняется — подождите мгновение';
/**
 * Загрузка и сохранение — разные ожидания, и путать их нельзя: человек, которому во время
 * первого открытия раздела сказали «правка сохраняется», решит, что он что-то нажал.
 */
const BUSY_LOADING = 'Настройки ещё загружаются — подождите мгновение';

export function createSettingsEditor(
  gateway: SettingsGateway,
  startingHints: DeviceHints = {},
): SettingsEditor {
  let settings: Settings = {};
  let ownKeys: readonly string[] = [];
  let ignoredKeys: readonly string[] = [];
  let groups: SettingGroups = {};
  let hints: DeviceHints = { ...startingHints };
  let loading = false;
  let saving = false;
  let refusal: string | null = null;
  const listeners = new Set<(state: SettingsState) => void>();

  const state = (): SettingsState => ({
    settings,
    ownKeys,
    ignoredKeys,
    groups,
    appearance: appearanceOf(settings, hints),
    loading,
    saving,
    refusal,
  });

  const tell = (): void => {
    const now = state();
    for (const listener of listeners) listener(now);
  };

  /**
   * Принять ответ сервера целиком: он знает про заготовку фирмы больше, чем экран.
   * Каждое поле кладётся новым объектом, а не ссылкой на ответ, — на это опирается откат:
   * снимок «как было» держит прежние объекты, и переписать их отсюда нельзя.
   */
  const accept = (answer: SettingsAnswer): void => {
    settings = { ...answer.settings };
    ownKeys = [...answer.ownKeys];
    ignoredKeys = [...answer.ignoredKeys];
    groups = { ...answer.groups };
  };

  return {
    state,

    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    async load() {
      // Второе чтение поверх незаконченного не начинаем, и молча: раздел и так вот-вот
      // получит свежее — ответом на первое чтение или на идущую правку. Пусти мы оба,
      // порядок ответов сервера ничем не задан, и человек остался бы с тем, что пришло
      // вторым, — то есть, может статься, со вчерашним видом поверх сегодняшнего.
      if (loading || saving) return;
      loading = true;
      refusal = null;
      tell();
      try {
        accept(await gateway.read());
      } catch (error) {
        refusal = explain(error);
      } finally {
        loading = false;
        tell();
      }
    },

    async change(key, value) {
      if (saving || loading) {
        refusal = saving ? BUSY_SAVING : BUSY_LOADING;
        tell();
        return;
      }
      // Что было до правки — на случай отказа. Копия, а не ссылка: иначе откатывать
      // будет некуда, мы же сами это и перепишем строкой ниже. Состав групп тоже здесь:
      // правка до первой загрузки застала бы раздел вовсе без групп, и откат оставил бы
      // человека с ответом сервера на неудавшуюся правку.
      const before = { settings, ownKeys, ignoredKeys, groups };

      settings = { ...settings, [key]: value };
      // Человек только что задал эту Настройку сам — значит с этой секунды её есть куда
      // сбрасывать, и кнопку сброса он должен увидеть сразу, а не после ответа сервера.
      ownKeys = ownKeys.includes(key) ? ownKeys : [...ownKeys, key].sort();
      saving = true;
      refusal = null;
      tell();

      try {
        accept(await gateway.update({ [key]: value }));
      } catch (error) {
        settings = before.settings;
        ownKeys = before.ownKeys;
        ignoredKeys = before.ignoredKeys;
        groups = before.groups;
        refusal = explain(error);
      } finally {
        saving = false;
        tell();
      }
    },

    async reset() {
      if (saving || loading) {
        refusal = saving ? BUSY_SAVING : BUSY_LOADING;
        tell();
        return;
      }
      // Здесь правки на глазах нет нарочно: что окажется на экране после сброса, знает
      // только сервер — там лежит заготовка фирмы. Угадай мы её сами, человек увидел бы
      // вид, который через мгновение сменится другим.
      const before = { settings, ownKeys, ignoredKeys, groups };
      saving = true;
      refusal = null;
      tell();

      try {
        accept(await gateway.reset());
      } catch (error) {
        settings = before.settings;
        ownKeys = before.ownKeys;
        ignoredKeys = before.ignoredKeys;
        groups = before.groups;
        refusal = explain(error);
      } finally {
        saving = false;
        tell();
      }
    },

    updateHints(next) {
      hints = { ...next };
      tell();
    },

    dismissRefusal() {
      refusal = null;
      tell();
    },
  };
}
